import React from 'react';
import { StyledAddButton, StyledButton, StyledCancelButton } from '@/common/components/button/StyledButton';
import { PositionedModal } from 'common/components/PositionedModal';
import styled from 'styled-components';
import { AlertCircle, CheckCircle, CheckSquare, Clock, FileText, Lightbulb } from 'lucide-react';
import { SuggestedIntervention } from './api';
import { getPillStyleForRelevanceLabel } from './pillStyles';

export type FocusPlanAddModalProps = {
  open: boolean;
  suggestion: SuggestedIntervention;
  onClose: () => void;
  onAdd: () => void;
  isSaving: boolean;
  isSuggestionAlreadyAdded: boolean;
};

const FocusPlanAddModal: React.FC<FocusPlanAddModalProps> = ({
  open,
  suggestion,
  onClose,
  onAdd,
  isSaving,
  isSuggestionAlreadyAdded,
}) => (
    <PositionedModal open={open} onClose={onClose} size="small">
      <ModalContent>
            <ModalHeader>
              <HeaderTopRow>
              <ModalIntent>Add to your Focus Plan</ModalIntent>
                {suggestion.relevance_label && (
                  <ModalPill style={{
                    background: getPillStyleForRelevanceLabel(suggestion.relevance_label).background,
                    color: getPillStyleForRelevanceLabel(suggestion.relevance_label).color,
                    borderColor: getPillStyleForRelevanceLabel(suggestion.relevance_label).borderColor,
                  }}>
                    {suggestion.relevance_label}
                  </ModalPill>
                )}
              </HeaderTopRow>
                <ModalTitle>{suggestion.task || suggestion.recommendation_name}</ModalTitle>
              {suggestion.references && (
                <ModalSubtitle>{suggestion.supportingScience} • {suggestion.references}</ModalSubtitle>
              )}
            </ModalHeader>
            <ModalBody>
              {suggestion.description && (
                <ModalRow>
                  <ModalIcon>
                    <FileText size={20} color="#2563eb" />
                  </ModalIcon>
                  <ModalText>{suggestion.description}</ModalText>
                </ModalRow>
              )}
              {suggestion.practical_example && (
                <ModalRow>
                  <ModalIcon>
                    <Lightbulb size={20} color="#f59e0b" />
                  </ModalIcon>
                  <ModalText>{suggestion.practical_example}</ModalText>
                </ModalRow>
              )}
              {suggestion.softSkills && (
                <ModalRow>
                  <ModalIcon>
                    <CheckSquare size={20} color="#7c3aed" />
                  </ModalIcon>
                  <ModalText>{suggestion.softSkills}</ModalText>
                </ModalRow>
              )}
              {suggestion.time && (
                <ModalRow>
                  <ModalIcon>
                    <Clock size={20} color="#16a34a" />
                  </ModalIcon>
                  <ModalText>{suggestion.time}</ModalText>
                </ModalRow>
              )}
              {suggestion.time_estimate && suggestion.time_estimate !== suggestion.time && (
                <ModalRow>
                  <ModalIcon>
                    <Clock size={20} color="#16a34a" />
                  </ModalIcon>
                  <ModalText>{suggestion.time_estimate}</ModalText>
                </ModalRow>
              )}
              {suggestion.supportingScience && (
                <ModalRow>
                  <ModalIcon>
                    <FileText size={20} color="#2563eb" />
                  </ModalIcon>
                  <ModalText>{suggestion.supportingScience}</ModalText>
                </ModalRow>
              )}
              {Array.isArray(suggestion.why_question) && suggestion.why_question.length > 0 && (
                <div>
                  <ModalRow>
                    <ModalText className="font-semibold">Why this matters</ModalText>
                  </ModalRow>
                  {suggestion.why_question.map((bullet, idx) => (
                    <ModalRow key={idx}>
                      <ModalIcon>
                        <AlertCircle size={20} />
                      </ModalIcon>
                      <ModalText>{bullet}</ModalText>
                    </ModalRow>
                  ))}
                </div>
              )}
            </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            {isSuggestionAlreadyAdded ? (
              <>
                <AddedLabel>
                  <CheckCircle size={16} />
                  Added to Focus Plan
                </AddedLabel>
                <StyledCancelButton onClick={onClose} label="Close" />
              </>
            ) : (
              <>
                <StyledCancelButton onClick={onClose} />
                <StyledAddButton
                  label="Add to focus plan"
                  color="blue"
                  onClick={onAdd}
                  disabled={isSaving}
                  width="160px"
                />
              </>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </PositionedModal>
);

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 32px;
  font-family: 'Poppins', sans-serif;
`;


const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ModalIntent = styled.div`
  color: #475569;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  padding-bottom: 12px;
`;

const ModalTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
  max-width: 45ch;

`;

const ModalPill = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px 4px;
  border-radius: 9999px;
  background: #F3F4F6;
  color: #6B7280;
  border: 1px solid #D1D5DB;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  margin-top: 0;
`;

const ModalSubtitle = styled.div`
  color: #828b97;
  font-size: 0.85rem;
  line-height: 1.4;  

`;

const ModalBody = styled.div`
  display: grid;
  gap: 10px;
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 8px;
  align-items: start;
  padding-bottom: 4px;
`;

const ModalIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  border-radius: 10px;
  padding-top: 1px;
`;

const ModalText = styled.div`
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const AddedLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
  color: #16a34a;
  font-size: 0.95rem;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

export default FocusPlanAddModal;
